// Results page "Find a horse or course" (team#71).
// The index is read from the page's own finishing-order rows (no JSON blob):
// each .result-card has data-venue / data-course / data-time and its .fo-row
// children have data-horse / data-position / data-won / data-completed.
// Nothing typed is logged; the only request is the last-run lookup, sent to
// our own API when a horse has no run on the day.
import { normaliseName, nameMatches } from './name_match.js';
import { activeCourse, setCourse } from './course_chips.js';

const MIN_CHARS = 2;
const MAX_ITEMS = 20;

export function finishLabel(row) {
  const d = row.dataset;
  if (d.won === 'true') return 'Won';
  if (d.completed === 'false' || d.position === 'DNF') return "Didn't finish";
  return d.position || '';
}

export function buildIndex(root = document) {
  const runners = [];
  const courses = new Map();
  root.querySelectorAll('.result-card[id^="race-"]').forEach((card) => {
    const slug = card.dataset.course || '';
    const venue = card.dataset.venue || '';
    if (slug && !courses.has(slug)) courses.set(slug, venue);
    const time = (card.querySelector('.race-time') || {}).textContent || '';
    card.querySelectorAll('.fo-row[data-horse]').forEach((row) => {
      runners.push({
        name: row.dataset.horse, course: slug, venue, time: time.trim(),
        stamp: card.dataset.time || '', finish: finishLabel(row), card, row,
      });
    });
  });
  return { runners, courses };
}

// Pure: what to show for `query` given the chip in force. `view` is one of
// results | course-miss | none | short.
export function search(index, query, course) {
  if (normaliseName(query).length < MIN_CHARS) return { view: 'short', items: [] };
  const byTime = (a, b) => a.stamp.localeCompare(b.stamp);
  const horses = (all) => index.runners.filter((r) => (all || !course || r.course === course) && nameMatches(query, r.name))
    .sort((a, b) => normaliseName(a.name).localeCompare(normaliseName(b.name)) || byTime(a, b));
  const courseItems = [...index.courses].filter(([slug, venue]) => nameMatches(query, venue))
    .map(([slug, venue]) => ({ kind: 'course', slug, venue, current: slug === course }));
  const found = horses(false);
  const items = courseItems.concat(found.map((r) => ({ kind: 'horse', ...r }))).slice(0, MAX_ITEMS);
  if (found.length) return { view: 'results', items, total: courseItems.length + found.length };
  if (courseItems.length) return { view: 'results', items, total: courseItems.length };
  if (course && horses(true).length) {
    return { view: 'course-miss', items: [], venue: index.courses.get(course) || course };
  }
  return { view: 'none', items: [] };
}

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

export function initResultsSearch({
  root = document, history: hist = window.history, fetchFn = window.fetch ? window.fetch.bind(window) : null,
} = {}) {
  const box = root.querySelector('.results-search');
  if (!box) return;
  const input = box.querySelector('.rs-input');
  const list = box.querySelector('.rs-list');
  const note = box.querySelector('.rs-note');
  const status = box.querySelector('.rs-status');
  const clear = box.querySelector('.rs-clear');
  box.hidden = false;

  // The sticky v2 nav (tabs, detail toggle, date row) is taller than one line on
  // a phone, so the box sits at the nav's measured height, not a fixed guess.
  const bar = root.querySelector('.v2-nav');
  if (bar) {
    const page = box.closest('.v2-page') || box;
    const sync = () => page.style.setProperty('--v2-nav-h', bar.offsetHeight + 'px');
    sync();
    if (window.ResizeObserver) new ResizeObserver(sync).observe(bar);
    else window.addEventListener('resize', sync);
  }

  let index = null;
  let shown = [];
  let active = -1;
  let lookupTimer = null;
  let lookupAbort = null;

  const getIndex = () => (index = index || buildIndex(root));
  const course = () => activeCourse(root);

  function writeUrl(q) {
    const url = new URL(window.location.href);
    if (q) url.searchParams.set('q', q); else url.searchParams.delete('q');
    hist.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  function setActive(i) {
    const opts = list.querySelectorAll('[role="option"]');
    active = i;
    opts.forEach((o, n) => o.setAttribute('aria-selected', n === i ? 'true' : 'false'));
    if (i >= 0 && opts[i]) {
      input.setAttribute('aria-activedescendant', opts[i].id);
      if (opts[i].scrollIntoView) opts[i].scrollIntoView({ block: 'nearest' });
    } else input.removeAttribute('aria-activedescendant');
  }

  function open(isOpen) {
    list.hidden = !isOpen;
    input.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  function say(text, action) {
    note.replaceChildren();
    if (!text) { note.hidden = true; return; }
    note.hidden = false;
    note.appendChild(el('span', null, text));
    if (action) {
      const b = el('button', 'rs-action', action.label);
      b.type = 'button';
      b.addEventListener('click', action.run);
      note.appendChild(b);
    }
  }

  function lastRun(q) {
    if (!fetchFn) return;
    const before = box.dataset.date;
    if (!before) return;
    if (lookupAbort) lookupAbort.abort();
    lookupAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const url = '/api/results/last-run/?q=' + encodeURIComponent(q) + '&before=' + encodeURIComponent(before);
    fetchFn(url, lookupAbort ? { signal: lookupAbort.signal } : {})
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d || !d.horse_name || input.value.trim() !== q) return;
        const when = d.date ? ' on ' + d.date : '';
        say('No runs on this day. Last run: ' + d.horse_name + ', ' + d.course + when + (d.finish_label ? ', ' + d.finish_label : '') + '.');
        status.textContent = note.textContent;
      })
      .catch(() => {});
  }

  function render() {
    const q = input.value.trim();
    clear.hidden = !q;
    clearTimeout(lookupTimer);
    list.replaceChildren();
    shown = [];
    setActive(-1);
    say('');
    if (!q) { open(false); status.textContent = ''; return; }
    const res = search(getIndex(), q, course());
    if (res.view === 'short') { open(false); status.textContent = ''; return; }
    if (res.view === 'course-miss') {
      open(false);
      say(q + " isn't found at " + res.venue + '.', { label: 'Search all courses', run: () => { setCourse('', { root, history: hist }); input.focus(); } });
      status.textContent = note.textContent;
      return;
    }
    if (res.view === 'none') {
      open(false);
      say('No horse found for this day');
      status.textContent = 'No horse found for this day';
      lookupTimer = setTimeout(() => lastRun(q), 400);
      return;
    }
    shown = res.items;
    res.items.forEach((it, n) => {
      const li = el('li', 'rs-opt');
      li.id = 'rs-opt-' + n;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      if (it.kind === 'course') {
        li.appendChild(el('span', 'rs-name', it.venue));
        li.appendChild(el('span', 'rs-meta', it.current ? 'Showing this course' : 'Show this course'));
      } else {
        li.appendChild(el('span', 'rs-name', it.name));
        li.appendChild(el('span', 'rs-meta', it.venue + ', ' + it.time));
        li.appendChild(el('span', 'rs-fin', it.finish));
      }
      li.addEventListener('mousedown', (e) => e.preventDefault());
      li.addEventListener('click', () => choose(n));
      list.appendChild(li);
    });
    open(true);
    status.textContent = res.total + (res.total === 1 ? ' match' : ' matches');
  }

  function choose(n) {
    const it = shown[n];
    if (!it) return;
    open(false);
    if (it.kind === 'course') {
      if (!it.current) setCourse(it.slug, { root, history: hist });
      input.value = '';
      writeUrl('');
      clear.hidden = true;
      return;
    }
    root.querySelectorAll('.search-hit').forEach((x) => x.classList.remove('search-hit'));
    if (it.card.hidden) setCourse('', { root, history: hist });
    it.card.classList.add('search-hit');
    it.row.classList.add('search-hit');
    if (!it.card.classList.contains('expanded') && window.TL && TL.results) TL.results.toggleResult(it.card);
    else it.card.classList.add('expanded');
    if (window.TL && TL.results && TL.results.selectResult) TL.results.selectResult(it.row, it.card.dataset.raceId);
    if (it.card.scrollIntoView) it.card.scrollIntoView({ block: 'start' });
    status.textContent = it.name + ', ' + it.venue + ' ' + it.time + ', ' + it.finish;
  }

  input.addEventListener('input', () => { writeUrl(input.value.trim()); render(); });
  input.addEventListener('keydown', (e) => {
    const n = shown.length;
    if (e.key === 'ArrowDown' && n) { e.preventDefault(); open(true); setActive((active + 1) % n); }
    else if (e.key === 'ArrowUp' && n) { e.preventDefault(); open(true); setActive((active - 1 + n) % n); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(active); }
    else if (e.key === 'Escape') {
      if (!list.hidden) open(false);
      else if (input.value) { input.value = ''; writeUrl(''); render(); }
    }
  });
  clear.addEventListener('click', () => { input.value = ''; writeUrl(''); render(); input.focus(); });
  root.addEventListener('coursechange', () => { if (input.value.trim()) render(); });

  const q0 = new URLSearchParams(window.location.search).get('q');
  if (q0) { input.value = q0.slice(0, 80); render(); }
}
